<template>
  <div class="impact-page">
    <impact-header title="综合风险视图" :tags="['异动 + 请假合并打分', '谁最危险、先救谁']"
                   :chips="chips" :loaded-at="loadedAt"></impact-header>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心指标</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
      </div>
      <div class="section-filters">
        <filter-pills v-model="days" :options="dayOptions" label="时间窗口" @on-change="load"></filter-pills>
        <filter-pills v-model="source" :options="sourceOptions" label="来源" @on-change="load"></filter-pills>
        <span class="filter-note">※ 分档：极高 ≥55 / 高 40–54 / 中 25–39 / 低 &lt;25，上限 100 分</span>
      </div>
      <div class="kpi-grid">
        <Spin v-if="loading" fix></Spin>
        <kpi-card v-for="card in kpiCards" :key="card.key" v-bind="card"></kpi-card>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">风险构成</h2>
      </div>
      <p class="block-lead">对学生来说只有一件事：我的老师出问题了。两类影响合并到同一个学生身上，用统一口径打分排序。</p>
      <div class="grid g2">
        <div class="card">
          <div class="card-head">
            <span class="card-title">风险来源构成<span class="card-sub">按学生 · 两类叠加最危险</span></span>
          </div>
          <bar-list :rows="sourceRows" unit="人" :right-width="130"></bar-list>
          <div class="note is-warning">
            异动本来就伤一次，请假又在同一个人身上反复叠加。两类叠加的学生必须走「换老师 + 时段保障 + 主动沟通」的组合处理，单做一项不够。
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">风险等级分布<span class="card-sub">极高 ≥55 · 高 40–54 · 中 25–39 · 低 &lt;25</span></span>
          </div>
          <bar-list :rows="bandRows" unit="人" :right-width="110"></bar-list>
          <div class="note is-danger" v-if="bandInsight">{{ bandInsight }}</div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">综合风险榜</h2>
        <span class="section-right">近 {{ days }} 天 · 按风险分高 → 低 · 前 50 名，导出为全部</span>
      </div>
      <div class="grid">
        <div class="card">
          <div class="card-head">
            <span class="card-title">待处理名单<span class="card-sub">{{ sourceLabel }}</span></span>
            <span class="card-link" :class="{ 'is-disabled': exporting }" @click="doExport">{{ exporting ? '导出中…' : '导出待处理名单 →' }}</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr>
                <th class="num">#</th>
                <th>学生</th>
                <th>班主任</th>
                <th>原老师</th>
                <th>风险来源</th>
                <th class="num">异动</th>
                <th class="num">请假</th>
                <th class="num">3h内</th>
                <th class="num">损失课次</th>
                <th class="num">剩余课时</th>
                <th class="num">风险分</th>
                <th>等级</th>
                <th>建议动作</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in list" :key="row.uid">
                <td class="num t-muted">{{ row.rank }}</td>
                <td><span class="t-name">{{ row.userName || '—' }}</span><span class="t-id">{{ row.uid }}</span></td>
                <td>{{ row.adviserName || '—' }}</td>
                <td>{{ row.tchName || '—' }}</td>
                <td>
                  <span v-if="row.newPriority" class="mini-badge is-new">新生优先</span>
                  <span v-if="num(row.changeCount) > 0" class="mini-badge is-change">异动×{{ row.changeCount }}</span>
                  <span v-if="num(row.leaveCount) > 0" class="mini-badge is-leave">请假×{{ row.leaveCount }}</span>
                  <span v-if="row.source === 'both'" class="mini-badge is-both">叠加</span>
                </td>
                <td class="num">{{ num(row.changeCount) || '—' }}</td>
                <td class="num">{{ num(row.leaveCount) || '—' }}</td>
                <td class="num" :class="{ 't-danger': num(row.innerCount) > 0 }">{{ num(row.innerCount) || '—' }}</td>
                <td class="num"><b :class="{ 't-danger': num(row.lostLessons) >= 6 }">{{ fmtNum(row.lostLessons) }}</b></td>
                <td class="num">{{ fmtNum(row.remainHours) }}</td>
                <td class="num">
                  <span class="score-cell">
                    <span class="score-track"><div :style="{ width: Math.min(100, num(row.score)) + '%', background: scoreColor(row.score) }"></div></span>
                    <b>{{ fmtNum(row.score, 0) }}</b>
                  </span>
                </td>
                <td><span class="badge" :class="levelBadge(row.band)">{{ row.band || '—' }}</span></td>
                <td class="t-action">{{ row.action || '—' }}</td>
              </tr>
              <tr v-if="!list.length"><td colspan="13" class="empty">当前筛选下没有纳入评分的学生</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">打分口径</h2>
      </div>
      <p class="block-lead">可调，但每一项都要有业务理由。</p>
      <div class="grid g2">
        <div class="note">
          <b>风险分 = 异动 + 请假 + 叠加 + 敞口 + 损失</b>
          <ul>
            <li>异动：淘汰 / 离职 / 关闭时间 <b>统一每起 10 分</b></li>
            <li>异动结果修正：还没换老师 <b>+6</b>；换完但没保住原时段 <b>+4</b></li>
            <li>请假：每节 3 分；<b>3 小时内（含旷课）每节再加 4 分</b>（无代课兜底）</li>
            <li>两类叠加：<b>+7</b></li>
          </ul>
        </div>
        <div class="note is-warning">
          <ul>
            <li>课时敞口：剩余 ≥ 40 课时 +5；20–39 课时 +3</li>
            <li>损失课次：每课次 +1</li>
            <li>上限 100 分，四舍五入取整</li>
            <li>设计意图：异动权重高于请假，因为一次异动等于关系断裂；3 小时内的请假给到接近异动的单位权重，因为它同样没有兜底</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ImpactHeader from './parts/ImpactHeader.vue'
import KpiCard from './parts/KpiCard.vue'
import BarList from './parts/BarList.vue'
import FilterPills from './parts/FilterPills.vue'
import { postImpact, exportImpact, num, fmtNum, fmtRate, levelLow, DAY_OPTIONS, LEVEL_BADGE } from './parts/impact'

const SOURCE_COLORS = { both: '#E6464E', change: '#7C5CFF', leave: '#E89F2D' }
const BAND_META = {
  '极高': { label: '极高 ≥55', color: '#B3312F' },
  '高': { label: '高 40–54', color: '#E6464E' },
  '中': { label: '中 25–39', color: '#E89F2D' },
  '低': { label: '低 <25', color: '#8FA3C7' }
}

export default {
  name: 'TchImpactRisk',
  components: { ImpactHeader, KpiCard, BarList, FilterPills },
  data () {
    return {
      loading: false,
      exporting: false,
      loadedAt: '',
      days: 90,
      source: '',
      dayOptions: DAY_OPTIONS,
      sourceOptions: [
        { value: '', label: '全部来源' },
        { value: 'both', label: '两类叠加', color: SOURCE_COLORS.both },
        { value: 'change', label: '仅异动', color: SOURCE_COLORS.change },
        { value: 'leave', label: '仅请假/旷课', color: SOURCE_COLORS.leave }
      ],
      d: {}
    }
  },
  computed: {
    params () {
      return { days: this.days, source: this.source }
    },
    list () {
      return this.d.list || []
    },
    sourceLabel () {
      let hit = this.sourceOptions.filter(o => o.value === this.source)[0]
      return hit ? hit.label : '全部来源'
    },
    chips () {
      return [
        { label: '时间窗口', value: '近 ' + this.days + ' 天' },
        { label: '来源', value: this.sourceLabel },
        { label: '高风险线', value: '≥ 40 分' },
        { label: '极高线', value: '≥ 55 分' }
      ]
    },
    kpiCards () {
      let d = this.d
      return [
        {
          key: 'student',
          title: '纳入评分学生',
          value: fmtNum(d.studentCount),
          unit: '人',
          level: num(d.studentCount) > 0 ? 'warning' : 'healthy',
          period: '敞口 ' + fmtNum(d.exposureHours) + ' 课时',
          compare: '新生优先 ' + fmtNum(d.newCount) + ' 人',
          compareTone: num(d.newCount) > 0 ? 'danger' : 'neutral',
          foot: '窗口内至少被异动或请假波及一次的学生'
        },
        {
          key: 'high',
          title: '高风险学生',
          value: fmtNum(d.highCount),
          unit: '人',
          level: num(d.topCount) > 0 ? 'danger' : (num(d.highCount) > 0 ? 'warning' : 'healthy'),
          period: '占纳入学生 ' + fmtRate(d.highRate),
          compare: '极高 ' + fmtNum(d.topCount) + ' 人',
          compareTone: num(d.topCount) > 0 ? 'danger' : 'neutral',
          foot: '风险分 ≥ 40，未消课时合计 ' + fmtNum(d.highHours) + '，应当天进入人工跟进'
        },
        {
          key: 'both',
          title: '两类叠加',
          value: fmtNum(d.bothCount),
          unit: '人',
          level: num(d.bothCount) <= 0 ? 'healthy' : levelLow(d.bothAvg, 25, 40),
          period: '平均风险分 ' + fmtNum(d.bothAvg),
          compare: '单一来源 ' + fmtNum(d.singleAvg) + ' 分',
          compareTone: 'neutral',
          foot: '同时被异动和请假波及的学生'
        },
        {
          key: 'avg',
          title: '平均风险分',
          value: fmtNum(d.avgScore),
          unit: '分',
          level: levelLow(d.avgScore, 25, 40),
          period: 'P90 分位 ' + fmtNum(d.p90Score) + ' 分',
          compare: '',
          foot: '纳入学生风险分的算术平均，观察整体恶化趋势'
        }
      ]
    },
    sourceRows () {
      return (this.d.sourceGroups || []).map(function (g) {
        return {
          key: g.key,
          label: g.label,
          value: g.count,
          color: SOURCE_COLORS[g.key] || '#8FA3C7',
          extra: '均 ' + fmtNum(g.avgScore) + ' 分'
        }
      })
    },
    bandRows () {
      return (this.d.bands || []).map(function (b) {
        let meta = BAND_META[b.key] || { label: b.key, color: '#8FA3C7' }
        return { key: b.key, label: meta.label, value: b.count, color: meta.color, extra: fmtRate(b.rate) }
      })
    },
    bandInsight () {
      let d = this.d
      if (!num(d.studentCount)) {
        return ''
      }
      let top = num(d.topCount)
      let high = num(d.highCount)
      return '近 ' + this.days + ' 天：' + top + ' 人极高风险、' + (high - top) + ' 人高风险，合计 ' + high +
        ' 人需要人工介入，占纳入学生 ' + fmtRate(d.highRate) + '，未消课时合计 ' + fmtNum(d.highHours) + '。'
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    num: num,
    fmtNum: fmtNum,
    levelBadge (band) {
      return LEVEL_BADGE[band] || 'is-gray'
    },
    scoreColor (score) {
      let s = num(score)
      if (s >= 55) {
        return BAND_META['极高'].color
      }
      if (s >= 40) {
        return BAND_META['高'].color
      }
      return s >= 25 ? BAND_META['中'].color : BAND_META['低'].color
    },
    load () {
      let self = this
      self.loading = true
      postImpact(self, '/risk', self.params).then(function (data) {
        if (data) {
          self.d = data
          self.loadedAt = self.$moment().format('YYYY-MM-DD HH:mm:ss')
        }
      }).finally(function () {
        self.loading = false
      })
    },
    doExport () {
      if (this.exporting) {
        return
      }
      let self = this
      self.exporting = true
      exportImpact(self, Object.assign({ view: 'risk' }, self.params), '综合风险榜.xlsx').finally(function () {
        self.exporting = false
      })
    }
  }
}
</script>
<style scoped lang="less">
@import './parts/page.less';

.mini-badge {
  &.is-new {
    color: #fff;
    background: @danger;
  }
  &.is-change {
    color: @purple;
    background: rgba(124, 92, 255, .1);
  }
  &.is-leave {
    color: #C67511;
    background: rgba(232, 159, 45, .14);
  }
  &.is-both {
    color: @danger;
    background: rgba(230, 70, 78, .1);
  }
}

.impact-table td.t-action {
  font-size: 11px;
  color: @text-sub;
}

.grid > .note {
  margin-top: 0;
  background: @card;
}
</style>



// WEBPACK FOOTER //
// TchImpactRisk.vue?5cc889f4